import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { deviceApi } from '../../api/device.api';
import { useAsync } from '../../hooks/useAsync';
import { useTranslation } from '../../i18n/i18n';
import { DeviceType } from '../../models/device';
import useDevicesStore from '../../stores/devices.store';
import Banner from '../general/Banner';
import SegmentedControl from '../general/SegmentedControl';
import Spinner from '../general/Spinner';
import { ChevronRightIcon, SearchIcon } from '../icons/Icons';
import './DeviceList.scss';

type SearchMode = 'name' | 'uuid';

interface DeviceListProps {
  type: DeviceType;
  /** Translation prefix inside the `devices` namespace: `weather` or `geo`. */
  kind: 'weather' | 'geo';
  detailUrl: (uuid: string) => string;
}

const DeviceList = ({ type, kind, detailUrl }: DeviceListProps) => {
  const { t } = useTranslation('devices');
  const { t: tc } = useTranslation('common');
  const setCount = useDevicesStore((s) => s.setCount);
  const [mode, setMode] = useState<SearchMode>('name');
  const [query, setQuery] = useState('');
  const { data: devices, error, loading, reload } = useAsync(() => deviceApi.listAllByType(type), [type]);

  useEffect(() => {
    if (devices) setCount(type, devices.length);
  }, [devices, setCount, type]);

  const nameCounts = useMemo(() => {
    const counts = new Map<string, number>();
    (devices ?? []).forEach((d) => counts.set(d.name, (counts.get(d.name) ?? 0) + 1));
    return counts;
  }, [devices]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (devices ?? [])
      .filter((d) => !q || (mode === 'name' ? d.name.toLowerCase().includes(q) : d.uuid.toLowerCase().includes(q)))
      .sort((a, b) => a.name.localeCompare(b.name) || a.uuid.localeCompare(b.uuid));
  }, [devices, mode, query]);

  const hasDupes = rows.some((r) => (nameCounts.get(r.name) ?? 0) > 1);
  const total = devices?.length ?? 0;

  return (
    <section className="DeviceList">
      <div className="DeviceList__toolbar">
        <SegmentedControl<SearchMode>
          label={t('searchBy')}
          value={mode}
          onChange={setMode}
          options={[
            { value: 'name', label: t('byName') },
            { value: 'uuid', label: t('byUuid') },
          ]}
        />
        <label htmlFor="tp-search" className="visually-hidden">
          {t('searchLabel')}
        </label>
        <div className="DeviceList__search">
          <SearchIcon stroke="#4A5568" />
          <input
            id="tp-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={mode === 'name' ? t('placeholderName') : t(`${kind}.placeholderUuid`)}
            className={mode === 'uuid' ? 'DeviceList__input--mono' : undefined}
          />
        </div>
        {devices && <span className="DeviceList__count">{t(`${kind}.count`, { shown: rows.length, total })}</span>}
      </div>

      {hasDupes && (
        <Banner kind="info" className="DeviceList__banner">
          {t('duplicates')}
        </Banner>
      )}

      {error !== undefined && !loading ? (
        <Banner
          kind="error"
          className="DeviceList__banner"
          action={
            <button type="button" onClick={reload}>
              {tc('retry')}
            </button>
          }
        >
          {tc('errors.generic')}
        </Banner>
      ) : loading && !devices ? (
        <Spinner />
      ) : (
        <div className="DeviceList__scroll">
          <div className="DeviceList__table">
            <div className="DeviceList__head">
              <span>{t('columns.name')}</span>
              <span>{t('columns.uuid')}</span>
              <span />
            </div>
            {rows.map((d) => {
              const shared = nameCounts.get(d.name) ?? 0;
              return (
                <Link
                  key={d.uuid}
                  to={detailUrl(d.uuid)}
                  className="DeviceList__row"
                  aria-label={t('openAria', { name: d.name, uuid: d.uuid })}
                >
                  <span className="DeviceList__name">
                    <span>{d.name}</span>
                    {shared > 1 && <span className="DeviceList__dupe">{t('shareName', { count: shared })}</span>}
                  </span>
                  <code className="DeviceList__uuid">{d.uuid}</code>
                  <span className="DeviceList__open">
                    {t('open')}
                    <ChevronRightIcon />
                  </span>
                </Link>
              );
            })}
            {rows.length === 0 && (
              <div className="DeviceList__empty">{total === 0 ? t(`${kind}.none`) : t(`${kind}.empty`, { query })}</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default DeviceList;
